import { ContentCluster } from '@app/components/base/content-structure/ContentCluster';
import { WizardActionsRHF } from '@app/modules/audit/modules/aod/screens/aod-rules-add/wizard/actions/WizardActionsRhf';
import { yupResolver } from '@hookform/resolvers/yup';
import { AuditReportsGipEvidenceExportOptionsDto } from '@mi-front/api-v2';
import { Box, Grid, Stack } from '@mui/material';
import { ViewLoader } from '@ui/utils';
import React from 'react';
import { FormContainer, SelectElement, TextFieldElement } from 'react-hook-form-mui';
import { FormattedMessage, useIntl } from 'react-intl';
import { GipPathFormatCustomTextField } from './GipPathFormatCustomTextField';
import {
  audioMatchIncludeAfterLabelMessage,
  audioMatchIncludeBeforeLabelMessage,
  audioMatchIncludeHelperMessage,
} from './messages';
import {
  GIP_EXPORT_AUDIO_MATCH_INCLUDE_MAX,
  GIP_EXPORT_AUDIO_MATCH_INCLUDE_MIN,
  useGipExportSetup,
} from './useGipExportSetup';

export interface FormFieldsProps {
  initialConfig?: AuditReportsGipEvidenceExportOptionsDto;
  reportType: string;
  onSubmit: (data: AuditReportsGipEvidenceExportOptionsDto) => Promise<void>;
  ContentWrapper?: React.ComponentType<{ children: React.ReactNode }>;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Form for configuring GIP evidence export options (file and XLS settings).
 */
export function GipEvidenceExportConfigForm({
  initialConfig,
  reportType,
  onSubmit,
  ContentWrapper = React.Fragment,
  actions,
  children,
}: FormFieldsProps) {
  const { $t } = useIntl();
  const setup = useGipExportSetup(initialConfig, reportType);

  if (!setup) {
    return <ViewLoader />;
  }

  const audioMatchIncludeInputProps = {
    min: GIP_EXPORT_AUDIO_MATCH_INCLUDE_MIN,
    max: GIP_EXPORT_AUDIO_MATCH_INCLUDE_MAX,
    step: 5,
  };

  return (
    <FormContainer
      defaultValues={initialConfig ?? setup.initialValues}
      resolver={yupResolver(setup.validationSchema)}
      onSuccess={onSubmit}
    >
      <ContentWrapper>
        <ContentCluster
          title={
            <FormattedMessage
              id="audit.gipReport.export.filesConfig"
              defaultMessage="Configuración de archivos"
            />
          }
          divider={false}
        >
          <Stack spacing={4}>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <TextFieldElement
                  name="audioMatchIncludeBefore"
                  label={$t(audioMatchIncludeBeforeLabelMessage)}
                  type="number"
                  fullWidth
                  required
                  inputProps={audioMatchIncludeInputProps}
                  helperText={$t(audioMatchIncludeHelperMessage)}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <TextFieldElement
                  name="audioMatchIncludeAfter"
                  label={$t(audioMatchIncludeAfterLabelMessage)}
                  type="number"
                  fullWidth
                  required
                  inputProps={audioMatchIncludeInputProps}
                  helperText={$t(audioMatchIncludeHelperMessage)}
                />
              </Grid>
            </Grid>
            {setup.pathFormats && (
              <SelectElement
                name="pathFormat.formatId"
                label={
                  <FormattedMessage
                    id="audit.gipReport.export.pathFormat"
                    defaultMessage="Formato de path"
                  />
                }
                options={setup.pathFormats}
                fullWidth
                required
              />
            )}
            <GipPathFormatCustomTextField pathFormats={setup.pathFormats} />
          </Stack>
        </ContentCluster>

        <ContentCluster
          title={
            <FormattedMessage
              id="audit.gipReport.export.xlsConfig"
              defaultMessage="Configuración de XLS"
            />
          }
        >
          {setup.definitions.length > 0 ? (
            <SelectElement
              name="definitionId"
              label={
                <FormattedMessage
                  id="audit.gipReport.export.definition"
                  defaultMessage="Plantilla"
                />
              }
              options={setup.definitions}
              labelKey="name"
              fullWidth
              required
            />
          ) : (
            <Box>
              <FormattedMessage
                id="audit.gipReport.export.noTemplatesAvailable"
                defaultMessage="No hay plantillas disponibles"
              />
            </Box>
          )}
        </ContentCluster>
        {children}
      </ContentWrapper>
      {actions ? actions : <WizardActionsRHF />}
    </FormContainer>
  );
}
